import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatCurrency } from "@/lib/api";
import { logger } from "@/lib/logger";
import { TrendingUp, TrendingDown, History, Link2Off, Coins, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Timeline dialog for a non-real-estate investment (deposit, stock, ETF,
 * crypto, fund…). Lists every transaction linked via `linked_investment_id`
 * plus aggregate cash flow (invested / recovered / net) and per-year totals.
 *
 * Mirrors the layout of `RenovationsDialog` so users get a consistent feel
 * across "reformas" and "operaciones vinculadas".
 */
export default function InvestmentTimelineDialog({ open, onOpenChange, investment }) {
  const [data, setData] = useState(null);
  const [returnData, setReturnData] = useState(null);
  const [chain, setChain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState("all");

  const load = React.useCallback(async () => {
    if (!investment?.id) return;
    setLoading(true);
    try {
      const isDeposit = investment.type === "deposit";
      const [tl, rt, ch] = await Promise.all([
        api.get(`/investments/${investment.id}/linked-transactions`),
        api.get(`/investments/${investment.id}/return`).catch(() => ({ data: null })),
        isDeposit
          ? api.get(`/investments/${investment.id}/renewal-history`).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);
      setData(tl.data);
      setReturnData(rt.data);
      setChain(ch.data);
    } catch (e) {
      logger.error("investment timeline load", e);
    } finally {
      setLoading(false);
    }
  }, [investment?.id, investment?.type]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const unlink = async (txId) => {
    try {
      await api.patch(`/transactions/${txId}`, { linked_investment_id: "" });
      toast.success("Vínculo eliminado");
      load();
    } catch {
      toast.error("No se pudo desvincular");
    }
  };

  const currency = data?.currency || investment?.currency || "USD";
  const years = data
    ? Object.entries(data.by_year || {}).sort((a, b) => b[0].localeCompare(a[0]))
    : [];

  const filteredItems = React.useMemo(() => {
    if (!data?.transactions) return [];
    return data.transactions.filter((t) => {
      if (yearFilter !== "all" && !(t.date || "").startsWith(yearFilter)) return false;
      return true;
    });
  }, [data, yearFilter]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl bg-white max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="investment-timeline-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <History className="w-5 h-5 text-[#1C7C54]" />
            Movimientos · {investment?.name}
          </DialogTitle>
          <p className="text-sm text-[#5F7368]">
            Todo el dinero que ha entrado y salido de esta inversión, vinculado desde los movimientos bancarios.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="p-6 text-sm text-[#5F7368]">Cargando…</div>
        ) : !data ? (
          <div className="p-6 text-sm text-[#8A9992]">Sin datos</div>
        ) : (
          <>
            {/* Totals bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-xl border border-[#D95D39]/25 bg-[#D95D39]/5 p-3">
                <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#B44A2B] flex items-center gap-1">
                  <TrendingDown className="w-3 h-3" />
                  Invertido
                </div>
                <div className="font-display text-base font-semibold tabular-nums text-[#B44A2B]" data-testid="timeline-cash-out">
                  {formatCurrency(data.cash_out, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-[#1C7C54]/25 bg-[#1C7C54]/5 p-3">
                <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#1C7C54] flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  Recuperado
                </div>
                <div className="font-display text-base font-semibold tabular-nums text-[#1C7C54]" data-testid="timeline-cash-in">
                  {formatCurrency(data.cash_in, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-[#EBEBEA] p-3">
                <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Neto</div>
                <div className={`font-display text-base font-semibold tabular-nums ${data.net >= 0 ? "text-[#B44A2B]" : "text-[#1C7C54]"}`} data-testid="timeline-net">
                  {formatCurrency(data.net, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-[#EBEBEA] p-3">
                <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992] flex items-center gap-1">
                  <Coins className="w-3 h-3" />
                  Coste manual
                </div>
                <div className="font-display text-base font-semibold tabular-nums text-[#0A110E]" data-testid="timeline-manual-cost">
                  {formatCurrency(data.manual_cost_basis, currency)}
                </div>
              </div>
            </div>

            {/* Return card — shows XIRR when we have enough flow signal. */}
            {returnData && (
              <div className="rounded-xl border border-[#1C7C54]/25 bg-[#1C7C54]/5 p-3 mt-3" data-testid="timeline-return-card">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#1C7C54]">
                      Rentabilidad
                    </div>
                    <div className="font-display text-xl font-semibold tabular-nums text-[#0A110E] flex items-baseline gap-2">
                      {returnData.irr != null ? (
                        <>
                          <span data-testid="timeline-irr" className={returnData.irr >= 0 ? "text-[#1C7C54]" : "text-[#D95D39]"}>
                            {returnData.irr >= 0 ? "+" : ""}{returnData.irr.toFixed(2)}%
                          </span>
                          <span className="text-xs font-normal text-[#5F7368]">anualizada (IRR)</span>
                        </>
                      ) : (
                        <span className="text-sm font-normal text-[#8A9992]">
                          Añade movimientos vinculados para calcular la rentabilidad real
                        </span>
                      )}
                    </div>
                  </div>
                  {returnData.total_return_pct != null && (
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.08em] font-bold text-[#8A9992]">Retorno total</div>
                      <div className={`font-display text-base font-semibold tabular-nums ${returnData.total_return >= 0 ? "text-[#1C7C54]" : "text-[#D95D39]"}`} data-testid="timeline-total-return">
                        {returnData.total_return >= 0 ? "+" : ""}{formatCurrency(returnData.total_return, currency)}
                        {" "}
                        <span className="text-xs">({returnData.total_return_pct.toFixed(1)}%)</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-[11px] text-[#8A9992] mt-1">
                  Valor actual: <span className="tabular-nums text-[#0A110E]">{formatCurrency(returnData.current_value, currency)}</span>
                </div>
              </div>
            )}

            {/* Renewal chain — only when there are 2+ deposits in the chain */}
            {chain && chain.count >= 2 && (
              <div className="rounded-xl border border-[#C89A3F]/25 bg-[#C89A3F]/5 p-3 mt-3" data-testid="timeline-chain-card">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8B6B1F] flex items-center gap-1">
                    <RefreshCcw className="w-3 h-3" />
                    Historial de renovaciones · {chain.count} plazos
                  </div>
                  {chain.cagr_pct != null && (
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.08em] font-bold text-[#8A9992]">Compuesto (CAGR)</div>
                      <div className={`font-display text-base font-semibold tabular-nums ${chain.cagr_pct >= 0 ? "text-[#1C7C54]" : "text-[#D95D39]"}`} data-testid="timeline-chain-cagr">
                        {chain.cagr_pct >= 0 ? "+" : ""}{chain.cagr_pct.toFixed(2)}%
                      </div>
                    </div>
                  )}
                </div>
                <ol className="space-y-2">
                  {chain.chain.map((c, i) => (
                    <li
                      key={c.id}
                      data-testid={`chain-item-${c.id}`}
                      className={`flex items-center gap-3 rounded-lg px-2 py-1.5 ${c.is_current ? "bg-white border border-[#8B6B1F]/30" : ""}`}
                    >
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold bg-[#C89A3F]/20 text-[#8B6B1F] shrink-0">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#0A110E] truncate flex items-center gap-1">
                          {c.name}
                          {c.is_current && <span className="text-[10px] uppercase font-bold text-[#8B6B1F]">Actual</span>}
                        </div>
                        <div className="text-[11px] text-[#8A9992] tabular-nums">
                          {c.purchase_date} → {c.maturity_date} · TIN {c.interest_rate ?? "—"}%
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-semibold tabular-nums text-[#0A110E]">
                          {formatCurrency(c.principal, c.currency)}
                        </div>
                        {c.interest_earned > 0 && (
                          <div className="text-[11px] tabular-nums text-[#1C7C54]">
                            +{formatCurrency(c.interest_earned, c.currency)}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
                <div className="mt-3 text-[11px] text-[#8A9992] flex flex-wrap gap-3 tabular-nums">
                  <span>Capital inicial: <b className="text-[#0A110E]">{formatCurrency(chain.initial_principal, currency)}</b></span>
                  <span>Valor final: <b className="text-[#0A110E]">{formatCurrency(chain.final_value, currency)}</b></span>
                  <span>Intereses totales: <b className="text-[#1C7C54]">+{formatCurrency(chain.total_interest, currency)}</b></span>
                  <span>Años: <b className="text-[#0A110E]">{chain.years}</b></span>
                </div>
              </div>
            )}

            {/* By-year breakdown */}
            {years.length > 0 && (
              <div className="rounded-xl border border-[#EBEBEA] p-3 mt-3">
                <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992] mb-2">Movimiento por año</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {years.map(([year, v]) => (
                    <div key={year} data-testid={`timeline-year-${year}`} className="rounded-lg bg-[#FAFAF9] p-2">
                      <div className="text-[11px] text-[#8A9992]">{year}</div>
                      <div className="font-display text-sm font-semibold tabular-nums text-[#B44A2B]">
                        -{formatCurrency(v.out, currency)}
                      </div>
                      {v.in > 0 && (
                        <div className="text-[11px] tabular-nums text-[#1C7C54]">
                          +{formatCurrency(v.in, currency)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filter */}
            {data.transactions?.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Filtrar</span>
                <select
                  data-testid="timeline-filter-year"
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  className="h-7 px-2 rounded-md border border-[#EBEBEA] bg-white text-xs focus:outline-none focus:border-[#1C7C54]/60"
                >
                  <option value="all">Todos los años</option>
                  {years.map(([y]) => <option key={y} value={y}>{y}</option>)}
                </select>
                <span className="ml-auto text-xs text-[#5F7368]">
                  {filteredItems.length} mov
                </span>
              </div>
            )}

            <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-2" data-testid="timeline-list">
              {filteredItems.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#8A9992]">
                  <History className="w-8 h-8 mx-auto text-[#EBEBEA] mb-2" />
                  <div>
                    {data.transactions.length === 0
                      ? "Aún no hay movimientos vinculados a esta inversión."
                      : "Sin resultados para el filtro actual."}
                  </div>
                  {data.transactions.length === 0 && (
                    <div className="text-xs mt-1">
                      Vincula transacciones desde el detalle de la cuenta bancaria.
                    </div>
                  )}
                </div>
              ) : (
                <ul className="divide-y divide-[#EBEBEA]">
                  {filteredItems.map((t) => (
                    <li key={t.id} className="py-2 flex items-start gap-3" data-testid={`timeline-item-${t.id}`}>
                      <span className={`inline-block w-2 h-2 rounded-full mt-2 shrink-0 ${
                        t.amount < 0 ? "bg-[#D95D39]" : "bg-[#1C7C54]"
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#0A110E] truncate">
                          {t.description || "Movimiento"}
                        </div>
                        <div className="text-[11px] text-[#8A9992]">
                          {t.date}
                          {t.category && ` · ${t.category}`}
                          {t.account_bank && ` · ${t.account_bank}`}
                          {t.account_alias && ` — ${t.account_alias}`}
                        </div>
                      </div>
                      <div className={`text-sm font-semibold tabular-nums shrink-0 ${
                        t.amount < 0 ? "text-[#D95D39]" : "text-[#1C7C54]"
                      }`}>
                        {t.amount >= 0 ? "+" : ""}{formatCurrency(t.amount, t.currency || currency)}
                      </div>
                      <button
                        type="button"
                        onClick={() => unlink(t.id)}
                        title="Quitar vínculo"
                        data-testid={`timeline-unlink-${t.id}`}
                        className="p-1 rounded hover:bg-[#EBEBEA] text-[#8A9992] shrink-0"
                      >
                        <Link2Off className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
